'use client';

import { useState, useEffect, useCallback, type Dispatch, type SetStateAction } from 'react';

import { safeSessionStorage } from './safe-storage';

export function useSafeSessionStorage<T>(
  key: string,
  initialValue: T,
): [T, Dispatch<SetStateAction<T>>] {
  const [state, setState] = useState<T>(() => {
    const stored = safeSessionStorage.getItem(key);
    if (stored !== null) {
      try {
        return JSON.parse(stored) as T;
      } catch {
        return initialValue;
      }
    }
    return initialValue;
  });

  useEffect(() => {
    try {
      safeSessionStorage.setItem(key, JSON.stringify(state));
    } catch {}
  }, [key, state]);

  const setValue: Dispatch<SetStateAction<T>> = useCallback(
    value => {
      setState(prev => {
        const nextValue = typeof value === 'function' ? (value as (prev: T) => T)(prev) : value;
        return nextValue;
      });
    },
    [setState],
  );

  return [state, setValue];
}
